import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, computed, PropType } from "vue";
import { Field } from "vee-validate";
import { useDeviceStore } from "@/stores/deviceStore";
import { InputMode } from "@/types";
import DOMPurify from "dompurify";


const __default__ = {
  name: "BaseInput",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  type: {
    type: String,
    default: "text",
  },
  value: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  maxlength: {
    type: Number,
    default: 50,
  },
  errorMessage: {
    type: String,
  },
  successMessage: {
    type: String,
    default: "",
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: true,
  },
  inputmode: {
    type: String as PropType<InputMode>,
  },
  enableClearText: {
    type: Boolean,
    default: true,
  },
  rules: {
    type: Object,
  },
},
  emits: ["onFocused", "onBlurred", "onInput", "update:value"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const inputValue = computed({
  get(): string {
    return props.value as string;
  },
  set(inputValue: string) {
    const sanitizedValue = DOMPurify.sanitize(inputValue);
    emit("update:value", sanitizedValue);
  },
});

const deviceStore = useDeviceStore();
const inputField = ref<HTMLInputElement>();
const focusingInputField = ref<HTMLInputElement>();
const isFocused = ref(false);

const focusInputField = () => {
  inputField.value?.focus();
};

const onFocused = (event: Event) => {
  focusingInputField.value = event.target as HTMLInputElement;
  isFocused.value = true;
  emit("onFocused");
};

const onBlurred = () => {
  focusingInputField.value = undefined;
  isFocused.value = false;
  emit("onBlurred");
};

const onInput = (event: Event) => {
  const inputElement = event.target as HTMLInputElement;
  let sanitizedValue = DOMPurify.sanitize(inputElement.value);
  if (props.maxlength) {
    sanitizedValue = sanitizedValue.slice(0, props.maxlength);
  }
  inputElement.value = sanitizedValue;

  emit("update:value", sanitizedValue);
  emit("onInput");
};

const onChange = (event: Event) => {
  const inputElement = event.target as HTMLInputElement;
  if (props.type === "number" && props.maxlength) {
    inputElement.value = inputElement.value.slice(0, props.maxlength);
  }

  const sanitizedValue = DOMPurify.sanitize(inputElement.value);
  emit("update:value", sanitizedValue);
};

const shouldShowClearButton = computed(
  () => inputValue.value.length > 0 && isFocused.value
);

const clearText = () => {
  if (!inputValue.value || !inputField.value) return;

  const blank = "";
  // 키보드 문구추천 기능을 사용하는 경우에 input의 value가 남아있는 문제가 있어서 명시적으로 value를 지워줘야한다.
  inputField.value.value = blank;
  emit("update:value", blank);
  focusInputField();
};

watch(
  () => deviceStore.isShowingKeyboard,
  async (isShowing, _) => {
    if (!isShowing) return;

    focusingInputField.value?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  }
);

__expose({ inputField, focusInputField });

const __returned__ = { props, emit, inputValue, deviceStore, inputField, focusingInputField, isFocused, focusInputField, onFocused, onBlurred, onInput, onChange, shouldShowClearButton, clearText, get Field() { return Field } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})